import React from 'react';
import { Search, Filter, X } from 'lucide-react';
import { FallIncident } from '../types';

interface HistoryTabProps {
  incidents: FallIncident[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  filterBy: {
    severity: ('HIGH' | 'MEDIUM' | 'LOW')[];
    location: string[];
    status: ('RESOLVED' | 'UNDER_REVIEW' | 'PENDING')[];
  };
  toggleSeverityFilter: (severity: 'HIGH' | 'MEDIUM' | 'LOW') => void;
  toggleLocationFilter: (location: string) => void;
  toggleStatusFilter: (status: 'RESOLVED' | 'UNDER_REVIEW' | 'PENDING') => void;
  clearFilters: () => void;
}

const HistoryTab: React.FC<HistoryTabProps> = ({
  incidents,
  searchTerm,
  setSearchTerm,
  showFilters,
  setShowFilters,
  filterBy,
  toggleSeverityFilter,
  toggleLocationFilter,
  toggleStatusFilter,
  clearFilters
}) => {
  // Get unique locations for filter options
  const uniqueLocations = Array.from(
    new Set(incidents.map(incident => incident.location.room))
  );

  return (
    <div className="space-y-6">
      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search incidents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg border ${
              showFilters
                ? 'bg-blue-50 border-blue-500 text-blue-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="inline-block mr-2" size={20} />
            Filters
          </button>
          {(filterBy.severity.length > 0 ||
            filterBy.location.length > 0 ||
            filterBy.status.length > 0) && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-300"
            >
              <X className="inline-block mr-2" size={20} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Severity Filters */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Severity</h4>
              <div className="space-y-2">
                {['HIGH', 'MEDIUM', 'LOW'].map((severity) => (
                  <label
                    key={severity}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filterBy.severity.includes(severity as any)}
                      onChange={() => toggleSeverityFilter(severity as any)}
                      className="rounded text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{severity}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Location Filters */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Location</h4>
              <div className="space-y-2">
                {uniqueLocations.map((location) => (
                  <label
                    key={location}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filterBy.location.includes(location)}
                      onChange={() => toggleLocationFilter(location)}
                      className="rounded text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{location}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Status Filters */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Status</h4>
              <div className="space-y-2">
                {['RESOLVED', 'UNDER_REVIEW', 'PENDING'].map((status) => (
                  <label
                    key={status}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filterBy.status.includes(status as any)}
                      onChange={() => toggleStatusFilter(status as any)}
                      className="rounded text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{status}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Incidents Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date & Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Severity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Response Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {incidents.map((incident) => (
              <tr key={incident.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(incident.timestamp).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{incident.location.room}</div>
                  <div className="text-xs text-gray-500">
                    Zone: {incident.location.zone}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      incident.severity === 'HIGH'
                        ? 'bg-red-100 text-red-800'
                        : incident.severity === 'MEDIUM'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {incident.severity}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      incident.status === 'RESOLVED'
                        ? 'bg-green-100 text-green-800'
                        : incident.status === 'UNDER_REVIEW'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {incident.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {incident.responseTime}s
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-md">
                  <div className="truncate">{incident.description}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoryTab; 